/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery } from '@tanstack/react-query';

import type { GetTenantReleaseResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UseTenantReleaseQueryParams = {
  releaseId: string;
};

/**
 * Custom hook that fetches a single tenant release by ID.
 *
 * @param options Configuration object:
 *   - releaseId: The UUID of the release to fetch
 *
 * @returns Object containing:
 *   - data: TenantReleaseResponse for the requested release
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - refetch: Function to manually refetch data
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function useTenantReleaseByIdQuery({ releaseId }: UseTenantReleaseQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const query = useQuery({
    queryKey: ['tenant-release', selectedTenantId, releaseId],
    queryFn: async (): Promise<GetTenantReleaseResponse> => {
      if (!selectedTenantId) {
        throw new Error('No tenant selected');
      }

      if (!releaseId) {
        throw new Error('No release ID provided');
      }

      const response: GetTenantReleaseResponse = await mgmtPlaneAPIClient.tenantReleases.getTenantRelease({
        tenantId: selectedTenantId,
        releaseId,
      });

      return response;
    },
    enabled: !!selectedTenantId && !!releaseId,
    staleTime: 10 * 1000, // 10 seconds
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
